/**
 * Result Enter Popup – Histopathology result entry dialog.
 * Exact match of renderResultUploadPopup() in admin4.0 HistopathologyReport.js.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NewToolsFieldWithMic from "../../Components/Common Components/NewToolsFieldWithMic";
import { ImagePaths } from "../../Utility/ImagePaths";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { getCachevalue } from "../../Utility/Services/CacheProviderService";

const closeImg = ImagePaths?.LabCloseButton?.default ?? ImagePaths?.LabCloseButton;

export default function ResultEnterPopup({
  open,
  onClose,
  signatureData = [],
  templateData = [],
  popupData = null,
  popupLoading = false,
  errorMessage,
  onSave,
  selectedServiceData,
  resultTextData: resultTextDataProp,
  editorDisable: editorDisableProp = false,
  isDispatchshown = true,
  postButtonLabel = "Send Approval",
  showOnlyApproveButton = false,
  hideSaveAndPostButtons = false,
  viewOnly = false,
  uploadedAttachment = [],
  resultPDF = [],
  showSubmittedBy = false,
  showRejectButton = false,
  onReject,
}) {
  const [selectedLabIncharge, setSelectedLabIncharge] = useState(null);
  const [selectedPathologist2, setSelectedPathologist2] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [userList, setUserList] = useState([]);
  const [selectedSubmittedBy, setSelectedSubmittedBy] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [resultTextData, setResultTextData] = useState("");
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);
  const [docPreviewUrl, setDocPreviewUrl] = useState("");
  const [docPreviewIsPdf, setDocPreviewIsPdf] = useState(false);
  // TIFF (and other formats browsers can't render inline) fall into this bucket
  // — we show a "Download to view" card instead of a broken <img>.
  const [docPreviewIsUnviewable, setDocPreviewIsUnviewable] = useState(false);
  const [docPreviewName, setDocPreviewName] = useState("");
  const [docPreviewLoading, setDocPreviewLoading] = useState(false);

  const esignOptions = popupData?.esign_options?.length ? popupData.esign_options : (popupData?.lab_incharge_options?.length ? popupData.lab_incharge_options : null);
  const leftSignOptions = esignOptions ?? (signatureData || []);
  const rightSignOptions = popupData?.pathologist_options?.length ? popupData.pathologist_options : (esignOptions ?? (signatureData || []));

  // Resolve label from HISTOPATHOLOGY_ESIGN_CONFIG format: "role_name - name" (e.g. "pathologist - kumar path").
  // When options come from popup (lab_incharge_options/pathologist_options) they may lack role_name; resolve from signatureData by id.
  const getSignOptionLabel = (option) => {
    if (!option || typeof option === "string") return option ?? "";
    let role = option.role_name ?? option.role ?? "";
    let name = option.name ?? "";
    if ((!role || !name) && option.id != null && signatureData?.length) {
      const fromConfig = signatureData.find((s) => s.id === option.id);
      if (fromConfig) {
        role = role || (fromConfig.role_name ?? fromConfig.role ?? "");
        name = name || (fromConfig.name ?? "");
      }
    }
    if (!name) name = ([option.login_fname, option.login_lname].filter(Boolean).join(" ").trim() || option.login_name) ?? "";
    if (role && name) return `${role} - ${name}`;
    if (name) return name;
    if (role) return role;
    return String(option.id ?? "");
  };

  const isSignOptionEqual = (a, b) => {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    if (typeof a === "object" && typeof b === "object" && a.id != null && b.id != null) return a.id === b.id;
    return a === b;
  };

  const isCompletedOrDispatched =
    selectedServiceData?.result_status === "Completed" ||
    selectedServiceData?.result_status === "Dispatched";

  const disableDropdowns = editorDisableProp || isCompletedOrDispatched || viewOnly;

  useEffect(() => {
    if (!open) {
      setSelectedLabIncharge(null);
      setSelectedPathologist2(null);
      setSelectedTemplate(null);
      setResultTextData("");
      setSelectedSubmittedBy(null);
      return;
    }
    // Load user list for the "Submitted By" dropdown. Leave the selection
    // blank so the person submitting explicitly picks a name.
    RestAPIService.getAll(`${Serviceurls.HISTOPATHOLOGY_USER_ACCESS_ROLE_MAPPING}?include_admin=1`)
      .then((res) => {
        if (res?.data?.status === "success") {
          setUserList(res.data.data || []);
        }
      })
      .catch(() => {});
    if (popupData) {
      setResultTextData(popupData.report_content ?? "");
      const opts = popupData.esign_options ?? popupData.lab_incharge_options ?? [];
      const rightOpts = popupData.pathologist_options ?? popupData.esign_options ?? [];
      const templates = Array.isArray(popupData.template_options) && popupData.template_options.length > 0 ? popupData.template_options : (Array.isArray(templateData) ? templateData : []);
      setSelectedLabIncharge(popupData.esign_left_id != null ? (opts.find((o) => o.id === popupData.esign_left_id) ?? null) : (popupData.lab_incharge_id != null ? (opts.find((o) => o.id === popupData.lab_incharge_id) ?? null) : null));
      setSelectedPathologist2(popupData.esign_right_id != null ? (rightOpts.find((o) => o.id === popupData.esign_right_id) ?? null) : (popupData.pathologist_2_id != null ? (rightOpts.find((o) => o.id === popupData.pathologist_2_id) ?? null) : null));
      setSelectedTemplate(popupData.result_template_id != null ? (templates.find((t) => t.id === popupData.result_template_id) ?? null) : null);
      return;
    }
    if (selectedServiceData) {
      setResultTextData(selectedServiceData?.result_data ?? resultTextDataProp ?? "");
      if (selectedServiceData?.esign_left_id && signatureData?.length) {
        const left = signatureData.find((s) => s.id === selectedServiceData.esign_left_id);
        setSelectedLabIncharge(left ?? null);
      } else setSelectedLabIncharge(null);
      if (selectedServiceData?.esign_right_id && signatureData?.length) {
        const right = signatureData.find((s) => s.id === selectedServiceData.esign_right_id);
        setSelectedPathologist2(right ?? null);
      } else setSelectedPathologist2(null);
      if (selectedServiceData?.template_name && Array.isArray(templateData) && templateData.length > 0) {
        const t = templateData.find((tpl) => tpl?.template_name === selectedServiceData?.template_name);
        setSelectedTemplate(t ?? null);
        if (t?.template_data) setResultTextData(t.template_data);
      } else setSelectedTemplate(null);
    }
  }, [open, selectedServiceData?.id, signatureData, templateData, popupData?.result_detail_id]);

  useEffect(() => {
    if (typeof resultTextDataProp === "string" && open && selectedServiceData && !selectedTemplate && !popupData) {
      setResultTextData(resultTextDataProp);
    }
  }, [open, resultTextDataProp, selectedServiceData?.id, popupData]);

  const handleClose = () => {
    setSelectedLabIncharge(null);
    setSelectedPathologist2(null);
    setSelectedTemplate(null);
    setResultTextData("");
    onClose?.();
  };

  const handleTemplateChange = (e, value) => {
    setSelectedTemplate(value);
    setResultTextData(value?.template_data ?? "");
  };

  // Editable list of previously-saved documents. Initial value comes from the BE
  // (popupData.result_document); user can remove entries from the chip strip and
  // the trimmed list is sent back on Save/Send Approval so the BE overwrites.
  const [existingDocs, setExistingDocs] = useState(() =>
    Array.isArray(popupData?.result_document) ? [...popupData.result_document] : []
  );
  useEffect(() => {
    setExistingDocs(Array.isArray(popupData?.result_document) ? [...popupData.result_document] : []);
  }, [popupData?.result_detail_id, popupData?.result_document]);
  const removeExistingDoc = async (index) => {
    // Persist the removal immediately (BE overwrites result_document) so a
    // popup close+reopen doesn't resurrect the deleted chip. The chip is only
    // dropped from local state after the DELETE returns success.
    const resultId = popupData?.result_detail_id ?? selectedServiceData?.result_detail_id ?? selectedServiceData?.action_id;
    if (!resultId) {
      // No id to persist against yet — fall back to UI-only removal.
      setExistingDocs((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    const trimmed = existingDocs.filter((_, i) => i !== index);
    const payload = {
      result_id: resultId,
      result_document: trimmed.length > 0 ? JSON.stringify(trimmed) : "",
    };
    try {
      const res = await RestAPIService.delete(Serviceurls.HISTOPATHOLOGY_REPORT_DATA, payload);
      if (res?.data?.status === "success") {
        setExistingDocs(trimmed);
      } else {
        errorMessage?.(res?.data?.message || "Failed to delete document");
      }
    } catch (err) {
      errorMessage?.(err?.response?.data?.message || err?.message || "Failed to delete document");
    }
  };

  const getS3Key = useCallback(async (doc_key) => {
    try {
      const res = await RestAPIService.getAllTwo(Serviceurls.AWS_URL_GET + "?doc_key=" + encodeURIComponent(doc_key));
      if (res?.data?.status === "success" && res?.data?.data) return res.data.data;
    } catch (e) {
      errorMessage?.(e?.response?.data?.message || "Error fetching document");
    }
    return null;
  }, [errorMessage]);

  const fetchAndConvertToBase64 = useCallback(async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader?.result);
        reader.onerror = () => reject(reader?.error);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      errorMessage?.(err?.message || "Error loading document");
    }
    return null;
  }, [errorMessage]);

  const handleViewDocument = useCallback(async (doc) => {
    const docKey = doc?.document_file;
    if (!docKey) return;
    setDocPreviewLoading(true);
    setDocPreviewOpen(true);
    setDocPreviewUrl("");
    setDocPreviewIsPdf(false);
    setDocPreviewIsUnviewable(false);
    setDocPreviewName(doc?.document_name || docKey.split("/").pop() || "document");
    const url = await getS3Key(docKey);
    setDocPreviewLoading(false);
    if (!url) {
      setDocPreviewOpen(false);
      return;
    }
    const ext = (docKey?.split(".").pop() || "").toLowerCase();
    // Browsers can't natively render these in <img>. Show a "download to view"
    // card instead of a broken image placeholder.
    const UNVIEWABLE_EXTS = new Set(["tif", "tiff", "doc", "docx", "xls", "xlsx", "csv"]);
    if (ext === "pdf") {
      fetchAndConvertToBase64(url).then((base64) => {
        if (base64) {
          const b = base64.replace("data:binary/octet-stream;", "data:application/pdf;");
          setDocPreviewUrl(b);
          setDocPreviewIsPdf(true);
        }
      });
    } else if (UNVIEWABLE_EXTS.has(ext)) {
      setDocPreviewUrl(url);
      setDocPreviewIsUnviewable(true);
    } else {
      setDocPreviewUrl(url);
      setDocPreviewIsPdf(false);
    }
  }, [getS3Key, fetchAndConvertToBase64]);

  const handleCloseDocPreview = useCallback(() => {
    setDocPreviewOpen(false);
    setDocPreviewUrl("");
    setDocPreviewIsPdf(false);
    setDocPreviewIsUnviewable(false);
    setDocPreviewName("");
  }, []);

  const handleSaveResult = (resultHtml, isForSave, isForCompleted, pdfUploadList, capturedImg) => {
    // Guard here so the popup stays open when the required "Submitted By" is
    // empty — otherwise the parent's error toast fires but the popup closes
    // and the user has no chance to correct the value.
    if (showSubmittedBy && !selectedSubmittedBy?.username) {
      errorMessage?.("Please select Submitted By");
      return;
    }
    onSave?.(resultHtml, selectedLabIncharge, selectedPathologist2, { isForSave, isForCompleted, pdfUploadList, capturedImg, selectedTemplate, result_document: existingDocs, submitted_by: selectedSubmittedBy?.username || "" });
  };

  const handlePostResult = (resultHtml, isForSave, isForCompleted, pdfUploadList, capturedImg) => {
    if (showSubmittedBy && !selectedSubmittedBy?.username) {
      errorMessage?.("Please select Submitted By");
      return;
    }
    onSave?.(resultHtml, selectedLabIncharge, selectedPathologist2, { isForSave, isForCompleted, pdfUploadList, capturedImg, selectedTemplate, result_document: existingDocs, submitted_by: selectedSubmittedBy?.username || "" });
    handleClose();
  };

  const handleDispatchResult = (resultHtml) => {
    onSave?.(resultHtml, selectedLabIncharge, selectedPathologist2, { isDispatch: true });
    handleClose();
  };

  if (!open) return null;

  return (
    <>
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose} PaperProps={{ sx: { overflow: "hidden" } }}>
      {popupLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
      <Box
        component="div"
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "85vh",
          minHeight: 400,
        }}
      >
        <Box
          component="div"
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "nowrap",
            flexShrink: 0,
            padding: "0.5vw",
            marginTop: "0.5vw",
            marginRight: "0.5vw",
            gap: 0,
          }}
        >
          <Autocomplete
            value={selectedLabIncharge || null}
            options={leftSignOptions}
            disabled={disableDropdowns}
            getOptionLabel={getSignOptionLabel}
            isOptionEqualToValue={isSignOptionEqual}
            size="small"
            onChange={(e, value) => setSelectedLabIncharge(value)}
            sx={{ width: "15vw", minWidth: 160, marginRight: "1vw" }}
            renderInput={(params) => (
              <TextField {...params} label="Select Left E-Sign" />
            )}
          />
          <Autocomplete
            value={selectedPathologist2 || null}
            options={rightSignOptions}
            disabled={disableDropdowns}
            getOptionLabel={getSignOptionLabel}
            isOptionEqualToValue={isSignOptionEqual}
            size="small"
            onChange={(e, value) => setSelectedPathologist2(value)}
            sx={{ width: "15vw", minWidth: 160, marginRight: "2vw" }}
            renderInput={(params) => (
              <TextField {...params} label="Select Right E-Sign" />
            )}
          />
          <Autocomplete
            value={selectedTemplate || null}
            options={Array.isArray(templateData) ? templateData : []}
            disabled={disableDropdowns}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.template_name ?? ""
            }
            size="small"
            onChange={handleTemplateChange}
            sx={{ width: "20vw", minWidth: 180, marginRight: "2vw" }}
            renderInput={(params) => (
              <TextField {...params} label="Select Template" />
            )}
          />
          <Button
            className="Common_Btn_Min_Width"
            onClick={handleClose}
            sx={{ minWidth: 40, p: 0.5, flexShrink: 0 }}
          >
            <Box
              component="img"
              src={
                typeof closeImg === "object" && closeImg?.default
                  ? closeImg.default
                  : closeImg
              }
              height="1vw"
              width="1vw"
              alt="Close"
              sx={{ minHeight: 20, minWidth: 20, display: "block" }}
            />
          </Button>
        </Box>
        {existingDocs.length > 0 && (
          <Box sx={{ flexShrink: 0, px: "0.5vw", pb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: "0.75vw" }}>
              Uploaded Images
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {existingDocs.map((doc, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography noWrap sx={{ maxWidth: "12vw", fontSize: "0.75vw" }}>
                    {doc?.document_name ?? "document"}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon fontSize="small" />}
                    onClick={() => handleViewDocument(doc)}
                    sx={{ textTransform: "capitalize", minWidth: 0, p: 0.25 }}
                  >
                    View
                  </Button>
                  {!viewOnly && !editorDisableProp && (
                    <IconButton
                      size="small"
                      onClick={() => removeExistingDoc(index)}
                      title="Remove document"
                      sx={{ padding: "2px", color: "#d32f2f" }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <NewToolsFieldWithMic
          FromTestReport={true}
          selectedServiceData={selectedServiceData}
          EditorDisable={editorDisableProp || viewOnly}
          isDispatchshown={isDispatchshown}
          Title="Result"
          EditorRef={resultTextData}
          handleSaveResult={handleSaveResult}
          handlePostResult={handlePostResult}
          handleDispatchResult={handleDispatchResult}
          overallHeight="100%"
          EditorHeight="53vh"
          ResultPDF={[...(uploadedAttachment || []), ...(resultPDF || [])]}
          postButtonLabel={postButtonLabel}
          showOnlyApproveButton={showOnlyApproveButton}
          hideSaveAndPostButtons={hideSaveAndPostButtons}
          viewOnly={viewOnly}
          micTextEditDisabled={true}
          footerLeadingSlot={showSubmittedBy ? (
            <Autocomplete
              value={selectedSubmittedBy || null}
              options={userList}
              disabled={disableDropdowns}
              size="small"
              getOptionLabel={(opt) => {
                if (!opt) return "";
                if (typeof opt === "string") return opt;
                const name = [opt.first_name, opt.last_name].filter(Boolean).join(" ");
                return name || opt.username || "";
              }}
              isOptionEqualToValue={(a, b) => a?.id === b?.id || a?.username === b?.username}
              onChange={(_e, value) => setSelectedSubmittedBy(value)}
              disableClearable
              sx={{ width: "15vw", minWidth: 160 }}
              renderInput={(params) => (
                <TextField {...params} label="Submitted By" required error={!selectedSubmittedBy} />
              )}
            />
          ) : null}
          footerActionSlot={showRejectButton ? (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => { setRejectReason(""); setRejectOpen(true); }}
              sx={{ textTransform: "capitalize", height: "2vw", minHeight: 32 }}
            >
              Reject
            </Button>
          ) : null}
          addSpaceOnTab={true}
          errorMsg={errorMessage}
        />
        </Box>
      </Box>
      )}
    </Dialog>
    {docPreviewOpen && (
      <Modal open onClose={handleCloseDocPreview}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "60vw",
            height: "80vh",
            bgcolor: "white",
            boxShadow: 24,
            p: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box display="flex" justifyContent="flex-end">
            <IconButton onClick={handleCloseDocPreview} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {docPreviewLoading ? (
              <CircularProgress size={32} />
            ) : docPreviewIsPdf ? (
              <iframe title="Document" src={docPreviewUrl} style={{ width: "100%", height: "75vh", border: "none" }} />
            ) : docPreviewIsUnviewable ? (
              // TIFF / DOC / XLS etc — browser can't render inline. Offer a
              // click-to-open link (opens in a new tab; the browser's built-in
              // handler for the file type takes over, or it downloads).
              <Box sx={{ textAlign: "center", p: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  {docPreviewName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                  Inline preview is not available for this file type.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => window.open(docPreviewUrl, "_blank", "noopener,noreferrer")}
                  sx={{ textTransform: "capitalize" }}
                >
                  Open / Download
                </Button>
              </Box>
            ) : (
              <img src={docPreviewUrl} alt="Document preview" style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }} />
            )}
          </Box>
        </Box>
      </Modal>
    )}
    {rejectOpen && (
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1vw", fontWeight: 700 }}>Reject Result</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            minRows={4}
            maxRows={8}
            fullWidth
            label="Reason for rejection *"
            required
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
            error={!rejectReason.trim()}
            helperText={!rejectReason.trim() ? "Reason is required" : `${rejectReason.length}/500`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={!rejectReason.trim()}
            onClick={() => {
              const reason = rejectReason.trim();
              setRejectOpen(false);
              setRejectReason("");
              onReject?.(reason, { submitted_by: selectedSubmittedBy?.username || "" });
              handleClose();
            }}
          >
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>
    )}
  </>
  );
}
